import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import config from "../config/index.js";
import { useGeofence } from "../hooks/useGeofence.js";
import { useModalA11y } from "../hooks/useModalA11y.js";

const RESTRICTED = (config.restrictedRegions || []).map((c) => c.toUpperCase());

/**
 * First-visit V6.2 §A disclaimer + acknowledgement gate.
 *
 * The protocol is geofenced from restricted jurisdictions, but residents
 * outside the block-list must still confirm:
 *   1. The country they are accessing from (used to block US / SG even
 *      if the IP-based geofence resolver fails open).
 *   2. That they have read the risk disclosures (early-exit penalty,
 *      non-custodial nature, no FDIC protection).
 *
 * The country field is auto-filled from the IP-resolved geofence
 * country (`useGeofence`) but remains EDITABLE — VPNs / travelling
 * users / dev environments routinely have IP-vs-residence mismatch,
 * and the on-record acknowledgement is the user's own declaration of
 * residence, not their detected IP location.
 *
 * Until both boxes are ticked, the modal cannot be dismissed and the
 * underlying UI remains inert (the modal sits in a portal at z-[110]
 * above every page).
 */
export default function JurisdictionAckModal({ open, onConfirm, isSaving }) {
    const dialogRef = useRef(null);
    const [country, setCountry] = useState("");
    const [riskAck, setRiskAck] = useState(false);
    const [touched, setTouched] = useState(false);
    const [error, setError] = useState(null);
    const [userEdited, setUserEdited] = useState(false);
    const { country: detectedCountry, isLoading: geoLoading } = useGeofence();

    // Auto-fill from the IP-resolved country code the moment the
    // geofence hook resolves — but ONLY if the user hasn't already
    // typed something. The "DEV" sentinel (build-time bypass) and any
    // restricted code are skipped so the field doesn't get pre-filled
    // with a blocking value the user has to delete first.
    useEffect(() => {
        if (userEdited || !detectedCountry) return;
        if (detectedCountry === "DEV") return;
        if (!/^[A-Z]{2}$/.test(detectedCountry)) return;
        setCountry(detectedCountry);
    }, [detectedCountry, userEdited]);

    useModalA11y({
        open,
        onClose: () => {
            /* explicitly NON-dismissible — the ack must be completed */
        },
        containerRef: dialogRef
    });

    if (!open) return null;

    const normalisedCountry = country.trim().toUpperCase();
    const isRestricted = RESTRICTED.includes(normalisedCountry);
    const valid = /^[A-Z]{2}$/.test(normalisedCountry) && !isRestricted && riskAck;

    async function handleSubmit() {
        setTouched(true);
        if (!valid) return;
        try {
            await onConfirm({
                jurisdictionCode: normalisedCountry,
                riskDisclosureAcknowledged: riskAck
            });
            setError(null);
        } catch (err) {
            setError(err && err.message ? err.message : "Unable to save acknowledgement.");
        }
    }

    return createPortal(
        <div
            className="fixed inset-0 z-[110] flex items-center justify-center bg-[#0E2C19]/85 backdrop-blur-md p-4"
            role="presentation"
        >
            <div
                ref={dialogRef}
                className="w-full max-w-lg overflow-hidden rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] shadow-2xl"
                role="dialog"
                aria-modal="true"
                aria-labelledby="jurisdiction-ack-title"
            >
                <div className="bg-[#162e1f] border-b border-[#bfb67b]/30 px-5 py-4 flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FBC206]">
                        <svg
                            className="h-5 w-5 text-[#1D3E29]"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M12 2 L4 6 V12 C4 17 8 21 12 22 C16 21 20 17 20 12 V6 Z" />
                        </svg>
                    </span>
                    <div className="min-w-0 flex-1">
                        <div className="text-[9px] font-semibold uppercase tracking-widest text-[#FBC206]">
                            Jurisdictional Disclosure
                        </div>
                        <h3
                            id="jurisdiction-ack-title"
                            className="mt-0.5 font-manrope text-[15px] font-bold text-[#e6c985]"
                        >
                            Confirm your jurisdiction & accept terms
                        </h3>
                    </div>
                </div>

                <div className="p-5 space-y-4">
                    <p className="text-[12px] leading-relaxed text-white/85">
                        GILDer is a decentralised, non-custodial bond protocol. The
                        frontend is not available to residents of the United States
                        or Singapore. By proceeding you confirm — under your own
                        responsibility — that you are accessing this service from
                        a permitted jurisdiction.
                    </p>

                    <label className="block">
                        <div className="flex items-center justify-between gap-2">
                            <span className="font-manrope text-[11px] font-semibold uppercase tracking-widest text-[#FBC206]">
                                Country of residence (ISO 2-letter code)
                            </span>
                            {geoLoading ? (
                                <span className="inline-flex items-center gap-1.5 text-[10px] text-[#e6c985]/60">
                                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#e6c985]/60" aria-hidden="true" />
                                    Detecting…
                                </span>
                            ) : null}
                        </div>
                        <input
                            type="text"
                            inputMode="text"
                            maxLength={2}
                            autoCapitalize="characters"
                            placeholder="e.g. GB, AE, CH"
                            value={country}
                            onChange={(e) => {
                                setCountry(e.target.value.replace(/[^A-Za-z]/g, "").slice(0, 2));
                                setUserEdited(true);
                                setError(null);
                            }}
                            className="mt-1 w-full rounded-md bg-[#0E2C19] px-3 py-2 font-manrope text-[14px] font-semibold uppercase tracking-widest text-[#e6c985] ring-1 ring-[#bfb67b]/40 focus:outline-none focus:ring-[#FBC206]"
                            aria-invalid={touched && !/^[A-Z]{2}$/.test(normalisedCountry)}
                        />
                        {touched && !/^[A-Z]{2}$/.test(normalisedCountry) ? (
                            <p className="mt-1 text-[11px] text-[#FBC206]">
                                Enter a two-letter ISO country code.
                            </p>
                        ) : null}
                        {isRestricted ? (
                            <p className="mt-1 text-[11px] text-[#FBC206]">
                                Service is not available in {normalisedCountry}.
                            </p>
                        ) : null}
                    </label>

                    <label className="flex items-start gap-2.5 text-[12px] leading-relaxed text-white/85">
                        <input
                            type="checkbox"
                            checked={riskAck}
                            onChange={(e) => setRiskAck(e.target.checked)}
                            className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-[#bfb67b] bg-[#0E2C19] text-[#FBC206] focus:ring-[#FBC206]"
                        />
                        <span>
                            I acknowledge the 20% early-exit penalty, the non-custodial
                            nature of the protocol, the absence of FDIC / deposit
                            insurance, and the smart-contract risk associated with my
                            participation.
                        </span>
                    </label>

                    {error ? (
                        <p className="text-[11px] font-semibold text-[#FBC206]">{error}</p>
                    ) : null}

                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            disabled={!valid || isSaving}
                            onClick={handleSubmit}
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#FBC206] px-5 py-2 text-[12px] font-semibold text-[#10281b] transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 disabled:translate-y-0 disabled:hover:shadow-none"
                        >
                            {isSaving ? "Saving..." : "Accept & Continue"}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
